import { SubtitleActionTextButton } from '@shared/components/core/alertNotification/CommonAlertNotificationStyles';
import Button from '@shared/components/core/button/CoreButton';
import { IBannerModel } from '@shared/models/alerts/IBannerModel';
import { IAlertNotificationTypes } from '@shared/models/components/IAlertNotificationProps';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import CurrentUserContainer from '../state/CurrentUserContainer';
import OrganizationContainer from '../state/OrganizationContainer';
import { useCurrentTrial } from '../useCurrentTrialFetching';

// todo: consider refactoring to use useRelativeTime or some similar logic
export default function useFreeTrialStatusBanner() {
    const { t } = useTranslation();
    const { organizationId, managedOrgId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const currentUserContainer = CurrentUserContainer.useContainer();
    const organizationContainer = OrganizationContainer.useContainer();
    const { mostRecentTrial, freeTrialOrg, canManageBilling } = useCurrentTrial();

    // days left calculation
    const today = new Date();
    const daysLeft = mostRecentTrial
        ? Math.ceil((new Date(mostRecentTrial.expirationDate).getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        : 0;
    const isOnHomePage = location.pathname === '/';

    const handleFreeTrial = useCallback(async () => {
        // validation
        if (!freeTrialOrg) {
            handleFreeTrialExpiration();
            return;
        }

        // remaining users calculation
        const maxMembers = freeTrialOrg?.organization.maxMembers ?? 0;

        let currMembers = 0;
        if (organizationContainer.currentOrg?.id === freeTrialOrg.organizationId) {
            currMembers = organizationContainer.currentOrg.currentMemberCount ?? 0;
        } else {
            const result = await organizationContainer.getOrganizationDetails(freeTrialOrg.organizationId);
            currMembers = result?.resultType === 'Ok' && result.data ? (result.data.currentMemberCount ?? 0) : 0;
        }
        const remainingUsers = Math.max(0, maxMembers - currMembers);
        const isMultiUserOrg = maxMembers > 1;

        const handleUpgrade = () => {
            // upgrade button
            if (isOnHomePage && isMultiUserOrg) {
                navigate(`/org/${freeTrialOrg.organizationId}`);
            } else {
                navigate(`/org/${freeTrialOrg.organizationId}/trial/redirect/upgrade`);
            }
        };

        const ActionComponent = canManageBilling && (
            <Button $size="small" startIcon="upgrade" onClick={handleUpgrade}>
                {t('common:upgrade')}
            </Button>
        );

        // current org member
        const canInviteMembers = freeTrialOrg?.canInviteMembers || freeTrialOrg?.isAdmin;

        let subtitle =
            remainingUsers == 0
                ? t('common:license.trial.noUsersSubtitle')
                : t('common:license.trial.subtitle', { count: remainingUsers });
        if (maxMembers === 1) {
            subtitle = t('common:license.trial.upgrade');
        }

        // prevent inviting members if email is not verified
        const SubtitleAction =
            remainingUsers > 0 && canInviteMembers ? (
                <SubtitleActionTextButton
                    disabled={currentUserContainer.currentUser?.isEmailVerified == false}
                    variant="context"
                    to={`/org/${freeTrialOrg.organizationId}/members/invites`}>
                    {t('common:organization.canInviteMembers.title')}
                </SubtitleActionTextButton>
            ) : null;

        const title =
            organizationContainer.memberships.length > 1 && isOnHomePage
                ? t('common:license.trial.title.multiple', {
                      days: daysLeft,
                      organization: freeTrialOrg.organization.name,
                  })
                : t('common:license.trial.title.single', { days: daysLeft });

        const freeTrialBanner: IBannerModel = {
            id: 'freeTrial',
            icon: 'user',
            title: title,
            subtitle: subtitle,
            color: 'accent' as IAlertNotificationTypes,
            children: ActionComponent,
            subtitleAction: SubtitleAction,
        };

        currentUserContainer.addUpgradeBanner(freeTrialBanner);
    }, [currentUserContainer.addUpgradeBanner, t, freeTrialOrg, mostRecentTrial]);

    const handleFreeTrialExpiration = useCallback(() => {
        currentUserContainer.removeUpgradeBanner('freeTrial');
    }, [currentUserContainer.removeUpgradeBanner]);

    useEffect(() => {
        const isTrial =
            currentUserContainer.currentUser &&
            freeTrialOrg &&
            mostRecentTrial &&
            daysLeft > 0 &&
            !currentUserContainer.userLicenseAccess?.hasLicense;

        const isValidPage =
            isOnHomePage ||
            (organizationId &&
                ((freeTrialOrg?.organizationId === organizationId && !managedOrgId) ||
                    freeTrialOrg?.organizationId === managedOrgId));

        if (isTrial && isValidPage) {
            handleFreeTrial();
        } else {
            handleFreeTrialExpiration();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [freeTrialOrg, mostRecentTrial, organizationId, currentUserContainer.userLicenseAccess]);
}
